import ForwardChevrons from '@griply/assets/src/icons/interface/forward-chevrons.svg?react';
import X from '@griply/assets/src/icons/interface/x.svg?react';
import {
  getHabitDayContentState,
  HabitDayContentStateType,
} from '@griply/utils';
import { format, isAfter, isSameDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { CheckCalendar } from 'shared/assets/icons';
import { DayContextMenu } from 'shared/components/ui/day-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import {
  ScheduleEntryState,
  ScheduleTargetEntry,
} from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './habit-week.style';

export type HabitWeekDayProps = {
  date: Timestamp;
  occurrences: Timestamp[];
  dateEntries: ScheduleTargetEntry[];
  onDate: (
    date: Timestamp,
    type: ScheduleEntryState,
    options?: { value?: number; increment?: number },
  ) => void;
  onDateManual: (date: Timestamp) => void;
  isPerfectDay?: boolean;
};

export const HabitWeekDay: React.FC<HabitWeekDayProps> = ({
  date,
  occurrences,
  dateEntries,
  onDate,
  onDateManual,
  isPerfectDay,
}) => {
  const today = useToday();
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const dateEntry = useMemo(
    () => dateEntries.find((entry) => isSameDay(entry.date, date)),
    [date, dateEntries],
  );

  const isOccurrence = useMemo(
    () =>
      !dateEntry?.hidden &&
      !!occurrences.find((occurrence) => isSameDay(occurrence, date)),
    [date, occurrences, dateEntry],
  );

  const completionPercentage = dateEntry?.value
    ? (dateEntry.value / (dateEntry.target ?? 1)) * 100
    : 0;
  const isFailure = dateEntry?.state === ScheduleEntryState.Failure;
  const isSkipped =
    dateEntry?.state === ScheduleEntryState.Skip && !dateEntry?.hidden;
  const isFuture = isAfter(date, today);

  const contentState = useMemo(
    () =>
      getHabitDayContentState({
        completionPercentage,
        value: dateEntry?.value ?? 0,
        target: dateEntry?.target ?? null,
        isSkipped,
        isFailure,
      }),
    [
      completionPercentage,
      dateEntry?.value,
      dateEntry?.target,
      isSkipped,
      isFailure,
    ],
  );

  const _onDate = () =>
    dateEntry?.onDateManual
      ? onDateManual(date)
      : onDate(date, ScheduleEntryState.Complete);

  const onManualEntryLogging = () => onDateManual(date);

  const _openMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    !isFuture && openMenu();
  };

  return (
    <>
      {(isNumber(dateEntry?.target) || isNumber(dateEntry?.value)) && (
        <Tooltip
          label={[dateEntry?.value ?? 0, dateEntry?.target || undefined]
            .filter(isNumber)
            .join('/')}
          referenceElement={containerRef}
        />
      )}

      <Styled.DayContainer ref={containerRef}>
        <Styled.DayButton
          onClick={_onDate}
          onContextMenu={_openMenu}
          data-value={date.getTime()}
          $isOccurrence={isOccurrence}
          $completionPercentage={completionPercentage}
          $isPerfect={!!isPerfectDay}
          $isSkipped={isSkipped}
          $isFailure={isFailure}
          $isFuture={isFuture}
        >
          {contentState.type === HabitDayContentStateType.Skip ||
          contentState.type === HabitDayContentStateType.Fail ? (
            <Styled.DayIcon>
              <Icon
                icon={
                  contentState.type === HabitDayContentStateType.Skip
                    ? ForwardChevrons
                    : X
                }
                strokeWidth={2.5}
              />
            </Styled.DayIcon>
          ) : (
            <Styled.DayLabel
              $isIcon={contentState.type === HabitDayContentStateType.Check}
            >
              {contentState.type === HabitDayContentStateType.Label ? (
                format(date, 'EEEEE')
              ) : contentState.type === HabitDayContentStateType.Check ? (
                <Icon icon={CheckCalendar} />
              ) : (
                formatNumberShort(contentState.value)
              )}
            </Styled.DayLabel>
          )}
        </Styled.DayButton>

        {menuOpen && !!onDate && (
          <DayContextMenu
            date={date}
            target={dateEntry?.target}
            onDate={onDate}
            onManual={onManualEntryLogging}
            referenceElement={containerRef}
            onClose={closeMenu}
          />
        )}
      </Styled.DayContainer>
    </>
  );
};
